import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Activity, Calendar, GripVertical, Layers, Mail, Phone, Search, Star, Users } from "lucide-react";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import useAdminAuth, { adminAuthHeader } from "@/hooks/useAdminAuth";
import AdminGate from "@/components/admin/AdminGate";
import AdminHeader from "@/components/admin/AdminHeader";
import { Dot, RevCard, Stat } from "@/components/admin/AdminWidgets";
import HotEventsPanel from "@/components/admin/HotEventsPanel";
import RevenueSparkline from "@/components/admin/RevenueSparkline";
import CopyPasswordLinkButton from "@/components/admin/CopyPasswordLinkButton";
import AutoLoginLinkButton from "@/components/admin/AutoLoginLinkButton";
import TestWebhookPanel from "@/components/admin/TestWebhookPanel";
import WhatsAppDripToggle from "@/components/admin/WhatsAppDripToggle";
import UserRowColorPicker, { rowBgForColor, ROW_COLORS } from "@/components/admin/UserRowColorPicker";
import DeleteUserButton from "@/components/admin/DeleteUserButton";
import ContactWhenToggle from "@/components/admin/ContactWhenToggle";
import UrgencyStarsPicker from "@/components/admin/UrgencyStarsPicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGE_LABEL = {
  registered: "Solo registrato",
  lesson_1: "Lezione 1", lesson_2: "Lezione 2", lesson_3: "Lezione 3",
  full_course: "Corso completo",
};
const STAGE_COLOR = {
  registered: "#94a3b8", lesson_1: "#3b82f6", lesson_2: "#10b981",
  lesson_3: "#f59e0b", full_course: "#d95e39",
};

/**
 * One draggable row of the users table.
 * - Drag handle (GripVertical) on the leftmost column ONLY initiates drag.
 * - Color dot opens a popover to set/clear the row color.
 * - Clicking anywhere else on the row navigates to the user detail page.
 */
function SortableUserRow({ user, onColorChange, onContactWhenChange, onUrgencyStarsChange, onDeleted, dragDisabled }) {
  const u = user;
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: u.id, disabled: dragDisabled });

  const rowBg = rowBgForColor(u.admin_color);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundColor: rowBg,
    opacity: isDragging ? 0.65 : 1,
    zIndex: isDragging ? 10 : "auto",
    position: "relative",
  };

  const navigate = useNavigate();
  const goToDetail = () => {
    // Save scroll position so we can restore it when admin clicks "Indietro".
    try { sessionStorage.setItem("admin_dash_scroll_y", String(window.scrollY)); } catch { /* noop */ }
    navigate(`/admin/utente/${u.id}`);
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      onClick={goToDetail}
      className="hover:brightness-[0.98] align-top cursor-pointer"
      data-testid={`admin-user-row-${u.id}`}
    >
      <TableCell className="w-[78px]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            aria-label={dragDisabled ? "Riordino disabilitato durante la ricerca" : "Trascina per riordinare"}
            disabled={dragDisabled}
            data-testid={`admin-drag-handle-${u.id}`}
            {...(dragDisabled ? {} : { ...attributes, ...listeners })}
            className="inline-flex items-center justify-center w-6 h-6 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 cursor-grab active:cursor-grabbing disabled:opacity-30 disabled:cursor-not-allowed"
            style={{ touchAction: "none" }}
          >
            <GripVertical size={16} />
          </button>
          <UserRowColorPicker
            userId={u.id}
            value={u.admin_color}
            onChange={(c) => onColorChange(u.id, c)}
          />
        </div>
      </TableCell>
      <TableCell className="text-sm" style={{ color: "var(--np-muted)" }}>
        {u.created_at ? new Date(u.created_at).toLocaleString("it-IT") : "—"}
      </TableCell>
      <TableCell>
        <p className="font-medium" style={{ color: "var(--np-primary)" }}>{u.name}</p>
        <p className="text-xs flex items-center gap-1.5 mt-0.5" style={{ color: "var(--np-muted)" }}><Mail size={11} /> {u.email}</p>
        <p className="text-xs flex items-center gap-1.5" style={{ color: "var(--np-muted)" }}><Phone size={11} /> {u.phone}</p>
      </TableCell>
      <TableCell>
        <span className="inline-flex items-center text-[11px] font-semibold px-2 py-1 rounded-full text-white" style={{ background: STAGE_COLOR[u.stage] || "#94a3b8" }}>
          {STAGE_LABEL[u.stage] || u.stage}
        </span>
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Dot on={u.has_access_l1} label="1" />
          <Dot on={u.has_access_l2} label="2" />
          <Dot on={u.has_access_l3} label="3" />
          <Dot on={u.has_access_full_course} label="✓" />
        </div>
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        {u.has_password ? (
          <AutoLoginLinkButton userId={u.id} variant="table" />
        ) : (
          <CopyPasswordLinkButton userId={u.id} variant="table" />
        )}
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <ContactWhenToggle
          userId={u.id}
          value={u.contact_when || "?"}
          onChange={onContactWhenChange}
        />
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <UrgencyStarsPicker
          userId={u.id}
          value={u.urgency_stars || 0}
          onChange={onUrgencyStarsChange}
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-between gap-2">
          {u.consultation_booked
            ? <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full">Richiesta</span>
            : <span className="text-xs italic" style={{ color: "var(--np-muted)" }}>—</span>}
          <DeleteUserButton userId={u.id} userName={u.name} onDeleted={onDeleted} />
        </div>
      </TableCell>
    </TableRow>
  );
}

// Persist filter/sort state across navigation (e.g. admin opens a contact,
// then hits "Indietro" — filters should still be applied when they land back
// on the funnel dashboard). Kept in sessionStorage so it survives the round
// trip but resets on a fresh browser session / logout.
const FILTERS_STORAGE_KEY = "admin_dash_filters_v1";
const _loadFilters = () => {
  try {
    const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return {
      query: typeof p.query === "string" ? p.query : "",
      tab: typeof p.tab === "string" ? p.tab : "funnel",
      colorFilter: new Set(Array.isArray(p.colorFilter) ? p.colorFilter : []),
      contactWhenFilter: ["all", "mattina", "pome", "indifferente", "?"].includes(p.contactWhenFilter) ? p.contactWhenFilter : "all",
      sortMode: p.sortMode === "date" ? "date" : "manual",
      // "Progresso L.1": "all" = no filter, "50pct" = only users who watched ≥50% (fired at 50%/80%/100% or lesson_1_completed).
      l1ProgressFilter: p.l1ProgressFilter === "50pct" ? "50pct" : "all",
      // "Urgenza" filter: 0 = tutti (no filter); 1..5 = mostra solo chi ha
      // ALMENO N stelle (es. 3 → include 3, 4, 5-star).
      urgencyStarsFilter: [0, 1, 2, 3, 4, 5].includes(Number(p.urgencyStarsFilter))
        ? Number(p.urgencyStarsFilter) : 0,
    };
  } catch { return null; }
};

export default function AdminFunnelDashboardPage() {
  const { authed, verifying, error: authError, busy, login, logout, expireSession } = useAdminAuth();
  const _initial = _loadFilters();  // read once at mount — persists cross-navigation
  const [users, setUsers] = useState([]);
  const [counts, setCounts] = useState({});
  const [events, setEvents] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [query, setQuery] = useState(_initial?.query ?? "");
  const [tab, setTab] = useState(_initial?.tab ?? "funnel");
  const [loading, setLoading] = useState(false);
  // Multi-select filter by row color. Empty Set = no filter (show all).
  const [colorFilter, setColorFilter] = useState(() => _initial?.colorFilter ?? new Set());
  // "Fascia oraria" quick filter: "all" (default) | "mattina" | "pome" | "indifferente" | "?"
  const [contactWhenFilter, setContactWhenFilter] = useState(_initial?.contactWhenFilter ?? "all");
  // Sort mode: "manual" (drag&drop order via sort_rank) or "date" (created_at desc).
  const [sortMode, setSortMode] = useState(_initial?.sortMode ?? "manual");
  // "≥50% Lezione 1" quick filter: "all" (default) | "50pct" (only users who
  // watched ≥50% of L1 — computed server-side via funnel_events $lookup).
  const [l1ProgressFilter, setL1ProgressFilter] = useState(_initial?.l1ProgressFilter ?? "all");
  // Urgency filter — 0 = mostra tutti; 1..5 = mostra solo lead con ALMENO N stelle.
  const [urgencyStarsFilter, setUrgencyStarsFilter] = useState(_initial?.urgencyStarsFilter ?? 0);

  // Persist filter changes so returning to /admin/dashboard from a contact
  // detail page keeps every user-set preference (search text, colour pills,
  // "da contattare oggi/domani", manual vs date sort, ≥50% L.1 toggle,
  // urgency stars threshold).
  useEffect(() => {
    try {
      sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({
        query, tab,
        colorFilter: Array.from(colorFilter),
        contactWhenFilter, sortMode, l1ProgressFilter, urgencyStarsFilter,
      }));
    } catch { /* noop */ }
  }, [query, tab, colorFilter, contactWhenFilter, sortMode, l1ProgressFilter, urgencyStarsFilter]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, e, r] = await Promise.all([
        axios.get(`${API}/admin/funnel-users`, { headers: adminAuthHeader() }),
        axios.get(`${API}/admin/funnel-events?limit=200`, { headers: adminAuthHeader() }),
        axios.get(`${API}/admin/revenue`, { headers: adminAuthHeader() }),
      ]);
      setUsers(u.data.users || []);
      setCounts(u.data.counts || {});
      setEvents(e.data.events || []);
      setRevenue(r.data || null);
    } catch (err) {
      if (err?.response?.status === 401) expireSession();
    } finally { setLoading(false); }
  }, [expireSession]);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  // Restore scroll position when admin returns from a detail page.
  const scrollRestoredRef = useRef(false);
  useEffect(() => {
    if (scrollRestoredRef.current) return;
    if (!authed || loading || users.length === 0) return;
    try {
      const saved = sessionStorage.getItem("admin_dash_scroll_y");
      if (saved) {
        const y = Number(saved);
        if (Number.isFinite(y) && y > 0) {
          // Defer to next paint so the table has rendered all rows.
          window.requestAnimationFrame(() => window.scrollTo({ top: y, behavior: "instant" in window ? "instant" : "auto" }));
        }
        sessionStorage.removeItem("admin_dash_scroll_y");
      }
    } catch { /* noop */ }
    scrollRestoredRef.current = true;
  }, [authed, loading, users.length]);

  const filtered = useMemo(() => {
    let list = users;
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((u) =>
        u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.phone?.toLowerCase().includes(q)
      );
    }
    if (colorFilter.size > 0) {
      list = list.filter((u) => u.admin_color && colorFilter.has(u.admin_color));
    }
    if (contactWhenFilter !== "all") {
      // Match server-side normalisation: legacy values collapse to "?".
      list = list.filter((u) => (u.contact_when || "?") === contactWhenFilter);
    }
    if (l1ProgressFilter === "50pct") {
      list = list.filter((u) => u.l1_watched_50pct);
    }
    if (urgencyStarsFilter > 0) {
      list = list.filter((u) => (u.urgency_stars || 0) >= urgencyStarsFilter);
    }
    if (sortMode === "date") {
      // Newest first by created_at (fallback to id for stability).
      list = [...list].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return list;
  }, [users, query, colorFilter, contactWhenFilter, sortMode, l1ProgressFilter, urgencyStarsFilter]);

  // Drag is disabled when filters/search/non-manual sort are active.
  const dragDisabled = Boolean(query.trim()) || colorFilter.size > 0 || contactWhenFilter !== "all" || sortMode !== "manual" || l1ProgressFilter !== "all" || urgencyStarsFilter > 0;

  const toggleColorFilter = useCallback((color) => {
    setColorFilter((prev) => {
      const next = new Set(prev);
      if (next.has(color)) next.delete(color); else next.add(color);
      return next;
    });
  }, []);
  const clearColorFilter = useCallback(() => setColorFilter(new Set()), []);

  const onDeleted = useCallback((userId) => {
    setUsers((prev) => prev.filter((u) => u.id !== userId));
  }, []);

  const onColorChange = useCallback((userId, color) => {
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, admin_color: color } : u)));
  }, []);

  const onContactWhenChange = useCallback((userId, contactWhen) => {
    setUsers((prev) => prev.map((u) => (u.id === userId
      ? { ...u, contact_when: contactWhen }
      : u)));
  }, []);

  const onUrgencyStarsChange = useCallback((userId, stars) => {
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, urgency_stars: stars } : u)));
  }, []);

  const onDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setUsers((prev) => {
      const oldIndex = prev.findIndex((u) => u.id === active.id);
      const newIndex = prev.findIndex((u) => u.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      // Persist the new order to the backend (full list = stable global ranks).
      axios.post(
        `${API}/admin/users/reorder`,
        { user_ids: next.map((u) => u.id) },
        { headers: adminAuthHeader() },
      )
        .then(() => toast.success("Ordine aggiornato"))
        .catch((err) => {
          const detail = err?.response?.data?.detail || err?.message || "Errore";
          toast.error(`Impossibile salvare l'ordine: ${detail}`);
        });
      return next;
    });
  }, []);

  if (verifying) return null;
  if (!authed) {
    return <AdminGate title="Admin · Funnel Dashboard" onSubmit={login} error={authError} busy={busy} />;
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--np-tertiary)" }}>
      <AdminHeader
        title="Admin · Funnel Dashboard" subtitle="Neuroscienza Pratica — Alessandro Fulgheri"
        icon={<Layers size={20} color="#fff" />}
        navLinks={[
          { label: "Lead Inbox", href: "/admin/leads" },
          { label: "Storico Eventi", href: "/admin/eventi", testid: "nav-admin-eventi" },
          { label: "Automazioni", href: "/admin/automazioni", testid: "nav-admin-automazioni" },
          { label: "Editor messaggi", href: "/admin/messaggi", testid: "nav-admin-messaggi" },
        ]}
        onRefresh={load} refreshing={loading} onLogout={logout}
      />

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10" data-testid="admin-dashboard">
        <HotEventsPanel events={events} limit={12} />

        {revenue && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="revenue-cards">
            <RevCard label="Incassato OGGI" cents={revenue.today_cents} accent />
            <RevCard label="Ultimi 7 giorni" cents={revenue.week_cents} />
            <RevCard label="Incassato TOTALE" cents={revenue.total_cents} note={`${revenue.purchase_count} acquisti`} />
          </div>
        )}

        {revenue && <RevenueSparkline days={30} perCustomer={revenue.per_customer || []} />}

        <WhatsAppDripToggle />

        <TestWebhookPanel />

        <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-7">
          <Stat label="Iscritti" value={counts.total || 0} icon={<Users size={16} />} />
          <Stat label="Lead senza password" value={counts.no_password || 0} accent={(counts.no_password || 0) > 0} />
          <Stat label="Lezione 1" value={counts.lesson_1 || 0} />
          <Stat label="Lezione 2" value={counts.lesson_2 || 0} accent />
          <Stat label="Lezione 3" value={counts.lesson_3 || 0} accent />
          <Stat label="Corso completo" value={counts.full_course || 0} accent />
          <Stat label="Consulenze" value={counts.consultation || 0} icon={<Calendar size={16} />} />
        </div>

        <div className="flex items-center gap-2 border-b border-[#e3eaf2] mb-6">
          <button onClick={() => setTab("funnel")} className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${tab === "funnel" ? "border-[var(--np-secondary)]" : "border-transparent"}`} style={{ color: tab === "funnel" ? "var(--np-secondary)" : "var(--np-muted)" }}>Utenti</button>
          <button onClick={() => setTab("events")} className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px inline-flex items-center gap-1.5 ${tab === "events" ? "border-[var(--np-secondary)]" : "border-transparent"}`} style={{ color: tab === "events" ? "var(--np-secondary)" : "var(--np-muted)" }}>
            <Activity size={14} /> Eventi funnel ({events.length})
          </button>
        </div>

        {tab === "funnel" ? (
          <>
            {/* Filters + sort + search bar */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4" data-testid="admin-users-toolbar">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--np-muted)" }}>
                  Filtra:
                </span>
                {Object.entries(ROW_COLORS).map(([key, { dot }]) => {
                  const active = colorFilter.has(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      data-testid={`color-filter-${key}`}
                      aria-pressed={active}
                      onClick={() => toggleColorFilter(key)}
                      className="inline-flex items-center justify-center w-7 h-7 rounded-full transition hover:scale-110"
                      style={{
                        background: dot,
                        border: key === "white" ? "1px solid #cbd5e1" : "none",
                        boxShadow: active
                          ? "0 0 0 2px #ffffff, 0 0 0 4px var(--np-primary)"
                          : "inset 0 0 0 1px rgba(0,0,0,0.05)",
                        opacity: colorFilter.size === 0 || active ? 1 : 0.4,
                      }}
                      title={`Filtra colore ${key}`}
                    />
                  );
                })}
                {colorFilter.size > 0 && (
                  <button
                    type="button"
                    onClick={clearColorFilter}
                    data-testid="color-filter-clear"
                    className="text-[12px] font-semibold underline hover:no-underline"
                    style={{ color: "var(--np-secondary)" }}
                  >
                    Pulisci filtri
                  </button>
                )}
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <div className="inline-flex rounded-lg border border-[#dbe3ec] bg-white p-0.5" data-testid="contact-when-filter">
                  {[
                    { key: "all", label: "Tutti" },
                    { key: "mattina", label: "Mattina" },
                    { key: "pome", label: "Pome" },
                    { key: "indifferente", label: "Indiff." },
                    { key: "?", label: "?" },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      data-testid={`contact-when-filter-${key}`}
                      onClick={() => setContactWhenFilter(key)}
                      className="px-3 py-1.5 text-xs font-semibold rounded-md transition"
                      style={{
                        background: contactWhenFilter === key ? "var(--np-secondary)" : "transparent",
                        color: contactWhenFilter === key ? "#ffffff" : "var(--np-muted)",
                      }}
                      title={key === "all" ? "Mostra tutti" : key === "?" ? "Non ancora chiesto" : `Fascia preferita: ${label.toLowerCase()}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {/* "≥50% L.1" progress filter — surfaces the warmest leads
                    (those who watched at least half of Lesson 1) so the
                    admin can prioritize warm outreach. Fired via funnel
                    milestone events (tempo_pagina_50pct_lezione_1 etc.). */}
                <div className="inline-flex rounded-lg border border-[#dbe3ec] bg-white p-0.5" data-testid="l1-progress-filter">
                  {[
                    { key: "all", label: "Tutti L.1", title: "Mostra tutti gli utenti" },
                    { key: "50pct", label: "≥50% L.1", title: `Mostra solo chi ha guardato ≥50% della Lezione 1${counts.l1_watched_50pct != null ? ` (${counts.l1_watched_50pct})` : ""}` },
                  ].map(({ key, label, title }) => (
                    <button
                      key={key}
                      type="button"
                      data-testid={`l1-progress-filter-${key}`}
                      onClick={() => setL1ProgressFilter(key)}
                      title={title}
                      className="px-3 py-1.5 text-xs font-semibold rounded-md transition inline-flex items-center gap-1"
                      style={{
                        background: l1ProgressFilter === key ? "#16a34a" : "transparent",
                        color: l1ProgressFilter === key ? "#ffffff" : "var(--np-muted)",
                      }}
                    >
                      {label}
                      {key === "50pct" && counts.l1_watched_50pct != null && (
                        <span
                          className="text-[10px] font-mono px-1 py-0 rounded"
                          style={{
                            background: l1ProgressFilter === key ? "rgba(255,255,255,0.25)" : "#e2e8f0",
                            color: l1ProgressFilter === key ? "#fff" : "#475569",
                          }}
                        >
                          {counts.l1_watched_50pct}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                <div className="inline-flex rounded-lg border border-[#dbe3ec] bg-white p-0.5" data-testid="sort-mode-toggle">
                  {[
                    { key: "manual", label: "Manuale" },
                    { key: "date", label: "Data" },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      data-testid={`sort-mode-${key}`}
                      onClick={() => setSortMode(key)}
                      className="px-3 py-1.5 text-xs font-semibold rounded-md transition"
                      style={{
                        background: sortMode === key ? "var(--np-primary)" : "transparent",
                        color: sortMode === key ? "#ffffff" : "var(--np-muted)",
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {/* Urgency stars filter — pick a minimum threshold. 0★ = show
                    all, 1..5 = show only leads rated ≥N stars. Shows a live
                    count badge for each level (5★=how many "hottest" leads). */}
                <div className="inline-flex items-center rounded-lg border border-[#dbe3ec] bg-white p-0.5" data-testid="urgency-stars-filter">
                  <button
                    type="button"
                    data-testid="urgency-stars-filter-0"
                    onClick={() => setUrgencyStarsFilter(0)}
                    title="Mostra tutti (ignora urgenza)"
                    className="px-2.5 py-1.5 text-xs font-semibold rounded-md transition"
                    style={{
                      background: urgencyStarsFilter === 0 ? "#f59e0b" : "transparent",
                      color: urgencyStarsFilter === 0 ? "#ffffff" : "var(--np-muted)",
                    }}
                  >
                    Urg.
                  </button>
                  {[1, 2, 3, 4, 5].map((n) => {
                    // "at least N" — sum counts of N..5 for the badge tooltip
                    const total = [n, n + 1, n + 2, n + 3, n + 4, n + 5]
                      .map((k) => Number((counts.urgency_stars || {})[String(k)] || 0))
                      .reduce((a, b) => a + b, 0);
                    const active = urgencyStarsFilter === n;
                    return (
                      <button
                        key={n}
                        type="button"
                        data-testid={`urgency-stars-filter-${n}`}
                        onClick={() => setUrgencyStarsFilter(n)}
                        title={`Almeno ${n}★ (${total} lead)`}
                        className="inline-flex items-center gap-0.5 px-1.5 py-1.5 text-xs font-semibold rounded-md transition"
                        style={{
                          background: active ? "#f59e0b" : "transparent",
                          color: active ? "#ffffff" : "var(--np-muted)",
                        }}
                      >
                        <Star
                          size={11}
                          strokeWidth={2}
                          style={{
                            color: active ? "#ffffff" : "#f59e0b",
                            fill: active ? "#ffffff" : "#f59e0b",
                          }}
                        />
                        <span className="text-[11px]">{n}+</span>
                      </button>
                    );
                  })}
                </div>
                <div className="relative w-full md:w-72">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--np-muted)" }} />
                  <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca utente…" className="pl-9 h-11 rounded-lg bg-white border-[#dbe3ec]" data-testid="admin-search-input" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[#e3eaf2] overflow-hidden">
              {filtered.length === 0 ? (
                <p className="px-6 py-12 text-center text-sm" style={{ color: "var(--np-muted)" }}>Nessun utente nel funnel.</p>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                  <SortableContext items={filtered.map((u) => u.id)} strategy={verticalListSortingStrategy}>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#f7fafd] hover:bg-[#f7fafd]">
                          <TableHead className="text-[12px] font-semibold uppercase tracking-wider w-[78px]">Ordine</TableHead>
                          <TableHead className="text-[12px] font-semibold uppercase tracking-wider">Iscritto</TableHead>
                          <TableHead className="text-[12px] font-semibold uppercase tracking-wider">Contatti</TableHead>
                          <TableHead className="text-[12px] font-semibold uppercase tracking-wider">Stage</TableHead>
                          <TableHead className="text-[12px] font-semibold uppercase tracking-wider">Lezioni</TableHead>
                          <TableHead className="text-[12px] font-semibold uppercase tracking-wider">Accesso rapido</TableHead>
                          <TableHead className="text-[12px] font-semibold uppercase tracking-wider">Fascia contatto</TableHead>
                          <TableHead className="text-[12px] font-semibold uppercase tracking-wider">Urgenza</TableHead>
                          <TableHead className="text-[12px] font-semibold uppercase tracking-wider">Consulenza</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody data-testid="admin-users-table-body">
                        {filtered.map((u) => (
                          <SortableUserRow
                            key={u.id}
                            user={u}
                            onColorChange={onColorChange}
                            onContactWhenChange={onContactWhenChange}
                            onUrgencyStarsChange={onUrgencyStarsChange}
                            onDeleted={onDeleted}
                            dragDisabled={dragDisabled}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </SortableContext>
                </DndContext>
              )}
            </div>
            {dragDisabled && filtered.length > 0 && (
              <p className="mt-2 text-[12px] italic text-right" style={{ color: "var(--np-muted)" }}>
                ℹ️ Disabilita filtri/ricerca e seleziona ordinamento &quot;Manuale&quot; per poter trascinare e riordinare.
              </p>
            )}
          </>
        ) : (
          <div className="bg-white rounded-2xl border border-[#e3eaf2] overflow-hidden">
            {events.length === 0 ? (
              <p className="px-6 py-12 text-center text-sm" style={{ color: "var(--np-muted)" }}>Nessun evento ancora.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#f7fafd] hover:bg-[#f7fafd]">
                    <TableHead className="text-[12px] font-semibold uppercase tracking-wider">Quando</TableHead>
                    <TableHead className="text-[12px] font-semibold uppercase tracking-wider">Evento</TableHead>
                    <TableHead className="text-[12px] font-semibold uppercase tracking-wider">Payload</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((e, i) => (
                    <TableRow key={`${e.created_at}-${e.event_name}-${i}`} className="hover:bg-[#fafcfe] align-top">
                      <TableCell className="text-xs whitespace-nowrap" style={{ color: "var(--np-muted)" }}>{e.created_at ? new Date(e.created_at).toLocaleString("it-IT") : "—"}</TableCell>
                      <TableCell className="text-sm font-medium" style={{ color: "var(--np-primary)" }}>{e.event_name}</TableCell>
                      <TableCell className="text-xs font-mono" style={{ color: "var(--np-muted)" }}>{JSON.stringify(e.payload).slice(0, 220)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
